import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowUpRight } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { RiskBadge } from "@/components/RiskBadge";
import { Input } from "@/components/ui/input";
import { formatTimestamp } from "@/data/mockData";
import { useSentinel } from "@/context/SentinelContext";
import { cn } from "@/lib/utils";

const FILTERS = ["ALL", "HIGH", "MEDIUM", "LOW"];

export default function Incidents() {
  const navigate = useNavigate();
  const { incidents, live } = useSentinel();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("ALL");

  const rows = useMemo(() => {
    return incidents.filter((i) => {
      const matchesFilter =
        filter === "ALL" ||
        i.level === filter ||
        (filter === "LOW" && i.level === "SAFE");
      const q = query.trim().toLowerCase();
      const matchesQuery =
        !q ||
        i.eventType.toLowerCase().includes(q) ||
        i.id.toLowerCase().includes(q) ||
        (i.status || "").toLowerCase().includes(q);
      return matchesFilter && matchesQuery;
    });
  }, [incidents, query, filter]);

  return (
    <DashboardLayout>
      <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-100">
            Incident History
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            Every evaluated event with its risk classification and resolution.
          </p>
        </div>
        <span className="w-fit font-mono text-[11px] uppercase tracking-[0.15em] text-slate-500">
          {rows.length} of {incidents.length} records · {live ? "Live · Supabase" : "Connecting…"}
        </span>
      </div>

      {/* Controls */}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search events, IDs, status…"
            data-testid="incident-search-input"
            className="h-10 border-sentinel-border bg-sentinel-deep pl-9 text-slate-100 focus-visible:ring-sentinel-accent"
          />
        </div>
        <div className="flex items-center gap-1.5">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`incident-filter-${f.toLowerCase()}`}
              className={cn(
                "rounded-md border px-3 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-widest transition-colors",
                filter === f
                  ? "border-sentinel-accent/50 bg-sentinel-accent/12 text-sentinel-accent"
                  : "border-sentinel-border text-slate-400 hover:border-sentinel-borderhover hover:text-slate-200"
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="mt-5 overflow-hidden rounded-lg border border-sentinel-border">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead>
              <tr className="border-b border-sentinel-border bg-sentinel-deep">
                {["Risk", "Score", "Event Type", "Date / Time", "Status", ""].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((inc) => (
                <tr
                  key={inc.id}
                  onClick={() => navigate(`/incidents/${inc.id}`)}
                  data-testid={`incident-row-${inc.id}`}
                  className="group cursor-pointer border-b border-sentinel-border/60 bg-sentinel-card transition-colors last:border-0 hover:bg-[#1a1e2c]"
                >
                  <td className="px-5 py-4">
                    <RiskBadge level={inc.level} />
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-base font-bold tabular text-slate-100">{inc.score}</span>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-sm font-medium text-slate-100">{inc.eventType}</p>
                    <p className="font-mono text-[11px] text-slate-500">{inc.id}</p>
                  </td>
                  <td className="px-5 py-4 font-mono text-xs text-slate-400">
                    {formatTimestamp(inc.timestamp)}
                  </td>
                  <td className="px-5 py-4">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-300">{inc.status}</span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <ArrowUpRight className="ml-auto h-4 w-4 text-slate-600 transition-colors group-hover:text-sentinel-accent" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {rows.length === 0 && (
          <div className="px-5 py-14 text-center text-sm text-slate-500" data-testid="incidents-empty-state">
            No incidents match your filters.
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
